import React, { createContext, useContext, useState, useEffect } from 'react';
import { deepLService } from '@/services/deepLService';
import { isSupportedLanguage } from '@/config/languages';

interface LanguageContextType {
  /** Effective language: the storefront language while a storefront is open, otherwise the platform language. */
  currentLanguage: string;
  /** Changes the platform (Sokoby) language — persisted. */
  setCurrentLanguage: (lang: string) => void;
  /** Platform language, never affected by storefronts. */
  platformLanguage: string;
  /** Scoped storefront language (not persisted as the platform language); null clears it. */
  setStorefrontLanguage: (lang: string | null) => void;
  isTranslationEnabled: boolean;
  setTranslationEnabled: (enabled: boolean) => void;
  isTranslationReady: boolean;
}

// Reuse one context object across hot reloads so an updated module never splits provider and consumers.
const g = globalThis as unknown as { __sokobyLanguageContext?: React.Context<LanguageContextType | undefined> };
const LanguageContext = g.__sokobyLanguageContext ?? (g.__sokobyLanguageContext = createContext<LanguageContextType | undefined>(undefined));

const COMMON_TEXTS = [
  'Accueil', 'Tarifs', 'Contact', 'Se connecter', "S'inscrire", 'Créer ma boutique',
  "Démarrer l'essai gratuit", 'En savoir plus', 'Changer de langue',
];

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [platformLanguage, setPlatformLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (isSupportedLanguage(saved)) return saved;
    const browserLang = navigator.language.split('-')[0];
    return isSupportedLanguage(browserLang) ? browserLang : 'fr';
  });
  const [storefrontLanguage, setStorefrontLanguageState] = useState<string | null>(null);
  const currentLanguage = storefrontLanguage ?? platformLanguage;

  // Translation is on by default; the old stored "false" (from when it was disabled) is ignored.
  const [isTranslationEnabled, setTranslationEnabled] = useState(() => {
    const saved = localStorage.getItem('translationEnabled.v2');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('language', platformLanguage);
  }, [platformLanguage]);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
    if (isTranslationEnabled && currentLanguage !== 'fr') {
      deepLService.preloadCommonTranslations(COMMON_TEXTS, currentLanguage).catch(() => {});
    }
  }, [currentLanguage, isTranslationEnabled]);

  useEffect(() => {
    localStorage.setItem('translationEnabled.v2', JSON.stringify(isTranslationEnabled));
  }, [isTranslationEnabled]);

  const handleLanguageChange = (lang: string) => {
    if (isSupportedLanguage(lang)) setPlatformLanguage(lang);
  };
  const setStorefrontLanguage = (lang: string | null) => {
    if (lang === null || isSupportedLanguage(lang)) setStorefrontLanguageState(lang);
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setCurrentLanguage: handleLanguageChange,
        platformLanguage,
        setStorefrontLanguage,
        isTranslationEnabled,
        setTranslationEnabled,
        isTranslationReady: deepLService.isReady(),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
}
